import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Button, Spinner, Typography } from "@material-tailwind/react";
import {
  ArrowUpTrayIcon,
  ChevronLeftIcon,
  DocumentIcon,
} from "@heroicons/react/24/outline";
import { usePortalAuth } from "../context/PortalAuthContext";
import { portalApi } from "../services/portalApi";
import { uploadWithProgress } from "../utils/uploadWithProgress";
import RichTextEditor from "../components/RichTextEditor";
import RevistaEmailPreview from "../components/RevistaEmailPreview";

function isPdf(file) {
  if (!file) return false;
  return file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
}

export default function PortalAdminRevistaIssue() {
  const { issueId } = useParams();
  const { user, isAuthenticated } = usePortalAuth();
  const navigate = useNavigate();
  const inputRef = useRef(null);

  const [issue, setIssue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [publishing, setPublishing] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [messageHtml, setMessageHtml] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await portalApi.adminRevistaIssue(issueId);
      setIssue(r.issue || null);
      if (!r.issue) setError("Ejemplar no encontrado");
      else if (r.issue.messageHtml) setMessageHtml(r.issue.messageHtml);
    } catch (err) {
      setError(err?.message || "No se pudo cargar");
      setIssue(null);
    } finally {
      setLoading(false);
    }
  }, [issueId]);

  useEffect(() => {
    load();
  }, [load]);

  async function uploadFile(file) {
    if (!file) return;
    if (!isPdf(file)) {
      toast.error("Solo se permiten archivos PDF");
      return;
    }
    setUploading(true);
    setUploadPct(0);
    try {
      const { uploadUrl } = await portalApi.adminRevistaUploadUrl(issueId, {
        contentType: "application/pdf",
      });
      await uploadWithProgress(uploadUrl, {
        body: file,
        contentType: "application/pdf",
        onProgress: setUploadPct,
      });
      setUploadPct(100);
      toast.success("PDF subido");
      await load();
    } catch (err) {
      toast.error(err?.message || "Error al subir PDF");
    } finally {
      setUploading(false);
    }
  }

  async function publish() {
    setPublishing(true);
    try {
      const r = await portalApi.adminRevistaPublish(issueId, { messageHtml });
      toast.success(
        r?.notifyQueued
          ? "Publicado — envío a suscriptores en cola"
          : "Publicado"
      );
      await load();
    } catch (err) {
      toast.error(err?.message || "No se pudo publicar");
    } finally {
      setPublishing(false);
    }
  }

  function onDrop(e) {
    e.preventDefault();
    setDragOver(false);
    if (uploading || issue?.status === "published") return;
    const file = e.dataTransfer?.files?.[0];
    uploadFile(file);
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user?.role !== "admin") return <Navigate to="/" replace />;

  const showDraftTools = issue && issue.status !== "published";

  return (
    <div className="mx-auto w-full max-w-5xl">
      <div className="text-center mb-8">
        <Typography
          variant="h3"
          className="font-sans text-brand-dark text-2xl sm:text-3xl font-semibold tracking-tight"
        >
          {issue?.title || "Ejemplar"}
        </Typography>
        <p className="mt-2 text-brand-gray text-sm sm:text-base leading-relaxed">
          Sube el PDF y publícalo cuando esté listo.
        </p>
      </div>

      <div className="flex justify-center mb-6">
        <Link
          to="/admin/revistas"
          className="inline-flex items-center gap-1 text-sm text-brand-lavender hover:underline"
        >
          <ChevronLeftIcon className="h-4 w-4" aria-hidden />
          Volver a Conexión Siete
        </Link>
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 text-brand-gray py-10">
          <Spinner className="h-5 w-5" /> Cargando…
        </div>
      ) : error ? (
        <div className="flex flex-col items-center gap-3 text-center">
          <p className="text-red-600 text-sm">{error}</p>
          <Button
            size="sm"
            variant="outlined"
            className="normal-case"
            onClick={() => navigate("/admin/revistas")}
          >
            Ir al listado
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="rounded-xl border border-brand-lavender/20 bg-white p-5 text-sm text-center">
            <p className="text-brand-gray">
              Estado:{" "}
              <strong className="text-brand-dark capitalize">{issue.status}</strong>
            </p>
            {issue.publishedAt ? (
              <p className="text-brand-gray mt-1">Publicado: {issue.publishedAt}</p>
            ) : null}
            {issue.downloadUrl ? (
              <p className="mt-2 flex flex-wrap justify-center gap-3">
                <Link
                  to={`/revistas/leer?u=${encodeURIComponent(issue.downloadUrl)}`}
                  className="text-brand-lavender font-medium hover:underline"
                >
                  Abrir en lector
                </Link>
                <a
                  href={issue.downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-brand-gray hover:underline"
                >
                  PDF directo
                </a>
              </p>
            ) : null}
          </div>

          {showDraftTools ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
              <div className="space-y-5">
                <div
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      inputRef.current?.click();
                    }
                  }}
                  onDragEnter={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    setDragOver(false);
                  }}
                  onDrop={onDrop}
                  onClick={() => !uploading && inputRef.current?.click()}
                  className={[
                    "rounded-xl border-2 border-dashed px-6 py-12 text-center cursor-pointer transition-colors",
                    dragOver
                      ? "border-brand-lavender bg-brand-lilac/40"
                      : "border-brand-lavender/35 bg-white hover:bg-brand-lilac/20",
                    uploading ? "opacity-60 pointer-events-none" : "",
                  ].join(" ")}
                >
                  <input
                    ref={inputRef}
                    type="file"
                    accept="application/pdf,.pdf"
                    className="hidden"
                    disabled={uploading}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = "";
                      uploadFile(f);
                    }}
                  />
                  {uploading ? (
                    <div className="flex flex-col items-center gap-3 text-brand-gray w-full max-w-xs mx-auto">
                      <Spinner className="h-8 w-8" />
                      <p className="text-sm font-medium text-brand-dark">
                        Subiendo PDF… {uploadPct}%
                      </p>
                      <div className="w-full h-2 rounded-full bg-brand-lilac/50 overflow-hidden">
                        <div
                          className="h-full bg-brand-lavender transition-[width] duration-150 ease-out"
                          style={{ width: `${uploadPct}%` }}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      {issue.s3Key ? (
                        <DocumentIcon className="h-10 w-10 text-brand-lavender" aria-hidden />
                      ) : (
                        <ArrowUpTrayIcon className="h-10 w-10 text-brand-lavender" aria-hidden />
                      )}
                      <p className="font-medium text-brand-dark">
                        {issue.s3Key
                          ? "Arrastra otro PDF para reemplazar"
                          : "Arrastra el PDF aquí"}
                      </p>
                      <p className="text-sm text-brand-gray">
                        o haz clic para seleccionar un archivo
                      </p>
                    </div>
                  )}
                </div>

                {issue.s3Key ? (
                  <div className="text-left">
                    <p className="text-sm font-medium text-brand-dark mb-1">
                      Mensaje para suscriptores
                    </p>
                    <p className="text-xs text-brand-gray mb-2">
                      Opcional. Se refleja a la derecha en tiempo real.
                    </p>
                    <RichTextEditor
                      value={messageHtml}
                      onChange={setMessageHtml}
                      disabled={publishing || uploading}
                      placeholder="Ej. Ya está disponible la edición de octubre…"
                    />
                    <div className="flex justify-center mt-4">
                      <Button
                        className="bg-brand-lavender text-white normal-case"
                        disabled={publishing || uploading}
                        onClick={publish}
                      >
                        {publishing ? "Publicando…" : "Publicar ejemplar"}
                      </Button>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="lg:sticky lg:top-24">
                <RevistaEmailPreview
                  title={issue.title || "Conexión Siete"}
                  messageHtml={messageHtml}
                />
              </div>
            </div>
          ) : null}

          {issue.status === "published" ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
              <p className="text-sm text-brand-gray text-center lg:text-left self-center">
                Este ejemplar ya está publicado. Los suscriptores reciben el link por correo.
              </p>
              <RevistaEmailPreview
                title={issue.title || "Conexión Siete"}
                messageHtml={issue.messageHtml || messageHtml}
              />
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
